import { theme, Modal, Tooltip, BodyMedium, SubtleButton } from '@arnold/common';
import styled from '@emotion/styled';
import { useState } from 'react';
import { CommentContent } from './CommentBox/CommentContent';
import { ChartOption, getAColorByHighlightLevel } from './ScaleQuestionWidget';
import { Statement } from '../../lib/reports/statementHelpers';
import { useTranslation } from 'react-i18next';
import { TeamWithType } from '../../lib/reports/queryAggregatedData';
import { Filter } from '../../lib/reportHelper';
import {
  BarChartContainer,
  BarChartBody,
  ChartBarBox,
  ChartBar,
  ChartBarValue,
  ChartBarSpan,
  TooltipContent,
  TooltipItem,
} from './StyledComponents';
const EMPTY_COMMENTS_MODAL_DATA = {
  show: false,
  statement: null,
  comments: [],
  totalCount: 0,
  filteredTotalCount: 0,
  nonRelevantTotalCount: 0,
};

type StatementChartProps = {
  statement: Statement;
  chartOptions: ChartOption[];
  showEscapeOption: boolean;
  showComments: boolean;
  teams?: TeamWithType[];
  compareLevelFilter?: Filter;
};

// chart visualization component
const ChartVisualization = ({
  statement,
  chartOptions,
  showEscapeOption,
  totalCount,
  filteredTotalCount,
  nonRelevantTotalCount,
}: {
  statement: Statement;
  chartOptions: ChartOption[];
  showEscapeOption: boolean;
  totalCount: number;
  filteredTotalCount: number;
  nonRelevantTotalCount: number;
}) => {
  const { t } = useTranslation('Report');

  if (!statement) return null;

  const bars = chartOptions
    .map((option, index) => {
      const count = statement.answers
        .filter((answer) => answer.optionIndex === option.index)
        .reduce((acc, answer) => acc + answer.count, 0);
      if (!count) return null;
      const percent = (count / filteredTotalCount) * 100;
      const optionsWithThisHighlightLevel = chartOptions.filter((o) => o.highLightLevel === option.highLightLevel);
      const color = getAColorByHighlightLevel(
        option.highLightLevel,
        optionsWithThisHighlightLevel.length,
        optionsWithThisHighlightLevel.indexOf(option),
      );
      return { index, percent, color };
    })
    .filter((bar): bar is NonNullable<typeof bar> => bar !== null);

  const MIN_LABEL_GAP_PERCENT = 4.5;
  let cumPercent = 0;
  const labelPositions: number[] = [];
  for (const bar of bars) {
    const midpoint = cumPercent + bar.percent / 2;
    cumPercent += bar.percent;
    const prev = labelPositions[labelPositions.length - 1];
    if (prev !== undefined && midpoint - prev < MIN_LABEL_GAP_PERCENT) {
      labelPositions.push(prev + MIN_LABEL_GAP_PERCENT);
    } else {
      labelPositions.push(midpoint);
    }
  }

  return (
    <div>
      <Tooltip
        delay={100}
        followCursor={false}
        html={
          <TooltipContent chartOptions={chartOptions}>
            {chartOptions.map((option, index) => {
              const count = statement.answers
                .filter((answer) => answer.optionIndex === index)
                .reduce((acc, answer) => acc + answer.count, 0);
              const percent = (count / filteredTotalCount) * 100 || 0;
              const optionsWithThisHighlightLevel = chartOptions.filter(
                (o) => o.highLightLevel === option.highLightLevel,
              );
              const highlightColor = getAColorByHighlightLevel(
                option.highLightLevel,
                optionsWithThisHighlightLevel.length,
                optionsWithThisHighlightLevel.indexOf(option),
              );
              return (
                <TooltipItem key={index}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: theme.spacing.c,
                    }}
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        backgroundColor: highlightColor,
                        borderRadius: '50%',
                      }}
                    ></div>
                    <div style={{ fontWeight: 700 }}>{percent.toFixed(0)}%</div>
                  </div>
                  <div style={{ fontWeight: 700, marginLeft: theme.spacing.e }}>
                    {t('numberOfRespondents', { count, postProcess: 'interval' })}
                  </div>
                  <div style={{ marginLeft: theme.spacing.e }}>{option.optionName}</div>
                </TooltipItem>
              );
            })}
            {showEscapeOption && (
              <TooltipItem>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: theme.spacing.c,
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      backgroundColor: theme.colors.text.secondary,
                      borderRadius: '50%',
                    }}
                  ></div>
                  <div style={{ fontWeight: 700 }}>
                    {t('numberOfRespondents', { count: nonRelevantTotalCount, postProcess: 'interval' })}
                  </div>
                </div>
                <div style={{ marginLeft: theme.spacing.e }}>{t('bubble:scaleEscapeOption')}</div>
              </TooltipItem>
            )}
          </TooltipContent>
        }
      >
        <div>
          <BarChartBody>
            {bars.map((bar) => (
              <ChartBarBox key={bar.index} width={bar.percent}>
                <ChartBar color={bar.color} />
              </ChartBarBox>
            ))}
          </BarChartBody>
          <ChartLabelsRow>
            {bars.map((bar, i) => (
              <ChartBarValue
                key={bar.index}
                data-no-break
                style={{
                  position: 'absolute',
                  left: `${labelPositions[i]}%`,
                  transform: 'translateX(-50%)',
                }}
              >
                {bar.percent.toFixed(0)}%
              </ChartBarValue>
            ))}
          </ChartLabelsRow>
        </div>
      </Tooltip>
      <ChartBarSpan>{filteredTotalCount + ' / ' + totalCount + ' ' + t('numberOfRespondentsIncluded')}</ChartBarSpan>
    </div>
  );
};

// main component
const StatementChart = ({
  statement,
  chartOptions,
  showEscapeOption,
  showComments,
  teams,
  compareLevelFilter,
}: StatementChartProps) => {
  const totalCount = statement.answers.reduce((acc, answer) => acc + answer.count, 0);
  const filteredTotalCount = statement.answers
    .filter((answer) => !Number.isNaN(answer.optionIndex) && answer.optionIndex >= 0)
    .reduce((acc, answer) => acc + answer.count, 0);
  const nonRelevantTotalCount = totalCount - filteredTotalCount;
  const [commentsModal, setCommentsModal] = useState<{
    show: boolean;
    statement: Statement | null;
    comments: Statement['comments'];
    totalCount: number;
    filteredTotalCount: number;
    nonRelevantTotalCount: number;
  }>(EMPTY_COMMENTS_MODAL_DATA);
  const { t } = useTranslation('Report');

  const getAllStatementsForTeam = (statement: Statement, team: TeamWithType): Statement => {
    const answers = statement.answers.filter(
      (answer) =>
        answer.teamId === team.team.id || (team.type === 'unit' && team.team.subTeams.includes(answer.teamId!)),
    );
    const comments = statement.comments.filter(
      (comment) =>
        comment.teamId === team.team.id || (team.type === 'unit' && team.team.subTeams.includes(comment.teamId!)),
    );
    return {
      ...statement,
      answers,
      comments,
    };
  };
  const optionIndexes = chartOptions.map((option) => option.index);

  return (
    <div className={'mt-7'} data-icom="report-statement-chart" data-cy="report-statement-chart">
      <StatementContainer>
        <StatementText containsTeams={(teams?.length || 0) > 0} data-cy="report-pulser-statement-title">
          {statement.text}
        </StatementText>
        <StatementChartContainer>
          {compareLevelFilter ? (
            teams?.map((team) => {
              const statementForTeam = getAllStatementsForTeam(statement, team);
              if (
                statementForTeam.answers.filter((answer) => optionIndexes.includes(answer.optionIndex)).length === 0
              ) {
                return null;
              }
              const totalCount = statementForTeam.answers.reduce((acc, answer) => acc + answer.count, 0);
              const filteredTotalCount = statementForTeam.answers
                .filter((answer) => !Number.isNaN(answer.optionIndex) && answer.optionIndex >= 0)
                .reduce((acc, answer) => acc + answer.count, 0);
              const nonRelevantTotalCount = totalCount - filteredTotalCount;
              const comments = statementForTeam.comments.filter(
                (comment) =>
                  comment.teamId === team.team.id ||
                  (team.type === 'unit' && team.team.subTeams.includes(comment.teamId!)),
              );

              return (
                <ChartVisualizationWithTeams key={`${statementForTeam.text}-${team.team.id}`}>
                  <ChartTeamName key={team.team.id}>{team.team.teamName || t('withoutSupervisor')}</ChartTeamName>
                  <ChartVisualizationContainer>
                    <ChartVisualization
                      statement={statementForTeam}
                      chartOptions={chartOptions}
                      showEscapeOption={showEscapeOption}
                      totalCount={totalCount}
                      filteredTotalCount={filteredTotalCount}
                      nonRelevantTotalCount={nonRelevantTotalCount}
                    />
                    {showComments &&
                      (comments.length > 0 ? (
                        <SubtleButton
                          onClick={() =>
                            setCommentsModal({
                              show: true,
                              statement: statementForTeam,
                              comments,
                              totalCount,
                              filteredTotalCount,
                              nonRelevantTotalCount,
                            })
                          }
                          className="hide-from-export"
                          style={{ alignSelf: 'start' }}
                          size="small"
                        >
                          {t('showComments', { count: comments.length })}
                        </SubtleButton>
                      ) : (
                        <ChartBarSpan className="hide-from-export">{t('noComments')}</ChartBarSpan>
                      ))}
                  </ChartVisualizationContainer>
                </ChartVisualizationWithTeams>
              );
            })
          ) : (
            <BarChartContainer>
              <ChartVisualization
                statement={statement}
                chartOptions={chartOptions}
                showEscapeOption={showEscapeOption}
                totalCount={totalCount}
                filteredTotalCount={filteredTotalCount}
                nonRelevantTotalCount={nonRelevantTotalCount}
              />

              {showComments &&
                !commentsModal.show &&
                (statement.comments.length > 0 ? (
                  <SubtleButton
                    onClick={() =>
                      setCommentsModal({
                        show: true,
                        statement,
                        comments: statement.comments,
                        totalCount,
                        filteredTotalCount,
                        nonRelevantTotalCount,
                      })
                    }
                    className="hide-from-export"
                    style={{ alignSelf: 'start' }}
                    size="small"
                  >
                    {t('showComments', { count: statement.comments.length })}
                  </SubtleButton>
                ) : (
                  <ChartBarSpan className="hide-from-export">{t('noComments')}</ChartBarSpan>
                ))}
            </BarChartContainer>
          )}
        </StatementChartContainer>
      </StatementContainer>

      {commentsModal.show && (
        <div style={{ width: '100%' }}>
          <Modal
            show={commentsModal.show}
            title={t('answersToQuestion')}
            titleStyle={{
              marginTop: theme.spacing.l,
              marginBottom: theme.spacing.l,
              marginLeft: theme.spacing.d,
              fontSize: theme.typography.heading.large.default.fontSize,
              fontWeight: theme.typography.heading.large.default.fontWeight,
            }}
            width={800}
            content={
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.l, marginLeft: theme.spacing.d }}
              >
                <StatementContainer>
                  <StatementText>{statement.text}</StatementText>
                  <StatementChartContainer>
                    <BarChartContainer>
                      <ChartVisualization
                        statement={commentsModal.statement as Statement}
                        chartOptions={chartOptions}
                        showEscapeOption={showEscapeOption}
                        totalCount={commentsModal.totalCount}
                        filteredTotalCount={commentsModal.filteredTotalCount}
                        nonRelevantTotalCount={commentsModal.nonRelevantTotalCount}
                      />
                    </BarChartContainer>
                  </StatementChartContainer>
                </StatementContainer>
                <CommentContentWrapper>
                  {commentsModal.comments.map((comment, index) => (
                    <CommentContent
                      key={index}
                      author={comment.author}
                      teamName={comment.teamName}
                      text={comment.text}
                      noShadow
                      wider
                    />
                  ))}
                </CommentContentWrapper>
              </div>
            }
            onHide={() => setCommentsModal(EMPTY_COMMENTS_MODAL_DATA)}
          />
        </div>
      )}
    </div>
  );
};

export default StatementChart;

const StatementContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding-right: ${theme.spacing.b};

  @media (max-width: ${theme.breakpoint.small}) {
    flex-direction: column;
  }
`;

const StatementText = styled(BodyMedium)<{ containsTeams?: boolean }>`
  color: ${theme.colors.text.primary};
  width: ${({ containsTeams }) => (containsTeams ? '33%' : '50%')};
  margin-right: ${theme.spacing.h};
  max-width: 40%;

  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
    max-width: 100%;
  }
`;

const StatementChartContainer = styled.div<{ compareLevelFilter?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ compareLevelFilter }) => (compareLevelFilter ? '50%' : '100%')};

  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
  }
`;

const ChartVisualizationWithTeams = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: ${theme.spacing.h};
  gap: ${theme.spacing.f};

  @media (max-width: ${theme.breakpoint.small}) {
    flex-direction: column;
    gap: 0;
  }
`;

const CommentContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${theme.spacing.f};

  @media (max-width: ${theme.breakpoint.small}) {
    gap: ${theme.spacing.k};
    flex-direction: row;
  }

  & > div {
    margin-bottom: ${theme.spacing.l};
  }

  & > .col-sm-4 {
    max-width: calc(33.33% - calc(${theme.spacing.f} / 3 * 2));
  }
`;

const ChartTeamName = styled(BodyMedium)`
  width: 50%;
`;

const ChartVisualizationContainer = styled.div`
  width: 90%;
`;

const ChartLabelsRow = styled.div`
  position: relative;
  height: 20px;
`;
